<script setup lang="ts">
  import { computed, nextTick, onMounted, useTemplateRef, watch } from 'vue'
  import { useRouter } from 'vue-router'
  import {
    testIds,
    type SearchContextType,
    type SearchFunctionMap,
    type SearchResult
  } from '@gat-ship/types'
  import {
    Command,
    CommandGroup,
    CommandItem,
    CommandList,
    Kbd
  } from '@gat-ship/vue-components'
  import {
    IconArrowDown,
    IconArrowUp,
    IconHistory,
    IconQuestion,
    IconSearch
  } from '@gat-ship/icons'
  import { globalUtils, useFormatting } from '@gat-ship/utils'
  import {
    useGlobalSearchDialog,
    type GlobalSearchDialogContext
  } from '../composables/global-search-context'
  import { useProvideGlobalSearch } from '../composables/useProvideGlobalSearch'
  import { useGlobalSearchContextMetadata } from '../composables/useGlobalSearchContextMetadata'
  import { useGlobalSearchPalette } from '../composables/useGlobalSearchPalette'
  import type { RecentSearch } from '../state/recent-search-storage'
  import GlobalSearchInput from './GlobalSearchInput.vue'
  import GlobalSearchHelpDialog from './GlobalSearchHelpDialog.vue'

  const props = defineProps<{ searchFunctions?: SearchFunctionMap }>()

  const { session, recentSearches } = useProvideGlobalSearch({
    searchFunctions: () => props.searchFunctions
  })
  const {
    searchInputValue,
    selectedSearchContext,
    isTagActive,
    isSearchInputEmpty,
    isSearching,
    searchResults,
    isEmptySearchResults,
    submit,
    selectContext,
    runSearch
  } = session

  const contextMetadata = useGlobalSearchContextMetadata()
  const { closeSearchCommand }: GlobalSearchDialogContext =
    useGlobalSearchDialog()
  const router = useRouter()
  const { formatPlural } = useFormatting()

  const { visibleRecentSearches, contextItems, groupedResults, flatResults } =
    useGlobalSearchPalette(session, recentSearches)

  const showRecentSearches = computed(
    () =>
      isSearchInputEmpty.value &&
      !isTagActive.value &&
      visibleRecentSearches.value.length > 0
  )

  const showContextItems = computed(
    () =>
      !isTagActive.value &&
      searchResults.value === undefined &&
      !isSearching.value
  )

  const showNoResults = computed(
    () =>
      !isSearchInputEmpty.value &&
      isEmptySearchResults.value &&
      !isSearching.value
  )

  const inputRef = useTemplateRef('inputRef')

  function focusInput() {
    inputRef.value?.focusInput()
  }

  function onRecentSelect(recent: RecentSearch) {
    runSearch(recent.searchString, recent.context)
    focusInput()
  }

  function onContextSelect(context: SearchContextType) {
    if (context === session.defaultSearchContext) {
      // "Search for X in All" doubles as the explicit submit — it also
      // records the recent search.
      submit()
    } else {
      selectContext(context)
    }
    focusInput()
  }

  function onShowAll(context: SearchContextType) {
    runSearch(searchInputValue.value, context)
    focusInput()
  }

  function onResultSelect(result: SearchResult) {
    result.actions.selected(router)
    closeSearchCommand()
  }

  function timeSince(timestamp: number): string {
    return globalUtils.date.timeSince(
      globalUtils.date.convertTimestampToISODateTimeString(timestamp)
    )
  }

  function resultsCountText(total: number): string {
    return `${total} ${formatPlural(total, { one: 'result', other: 'results' })}`
  }

  // Command forwards the inner ListboxRoot expose (highlightFirstItem & co).
  const commandRef = useTemplateRef<{ highlightFirstItem?: () => void }>(
    'commandRef'
  )

  function highlightFirstItem() {
    commandRef.value?.highlightFirstItem?.()
  }

  // reka-ui only re-highlights on input events; async list changes
  // (results arriving, sections toggling) need an explicit refresh so
  // Enter always has a target (US-26).
  watch(
    [searchResults, isSearching, searchInputValue, selectedSearchContext],
    async () => {
      await nextTick()
      highlightFirstItem()
    }
  )

  onMounted(() => {
    highlightFirstItem()
  })
</script>

<template>
  <Command ref="commandRef" :data-testid="testIds.globalSearch.dialog">
    <GlobalSearchInput ref="inputRef" />
    <CommandList class="max-h-[420px]">
      <template v-if="showRecentSearches">
        <div class="bg-muted border-b px-4 py-2 text-xs font-medium">
          Recent searches
        </div>
        <!-- CommandItem requires a CommandGroup ancestor (group context). -->
        <CommandGroup class="p-0">
          <CommandItem
            v-for="recent in visibleRecentSearches"
            :key="`${recent.searchString}-${recent.context}`"
            :value="`recent:${recent.context}:${recent.searchString}`"
            class="group gap-2 rounded-none px-4 py-2"
            :data-testid="testIds.globalSearch.item.recent(recent.searchString)"
            @select="onRecentSelect(recent)"
          >
            <IconHistory class="size-4" />
            <div class="text-sm">
              <span class="text-muted-foreground">Searched for: </span>
              <span class="font-medium">"{{ recent.searchString }}"</span>
              <span class="text-muted-foreground">
                in {{ contextMetadata[recent.context].title }}</span
              >
            </div>
            <span
              class="text-muted-foreground ml-auto text-xs group-data-[highlighted]:hidden"
            >
              {{ timeSince(recent.timestamp) }}
            </span>
            <Kbd class="ml-auto hidden group-data-[highlighted]:inline-flex">
              Enter
            </Kbd>
          </CommandItem>
        </CommandGroup>
      </template>

      <template v-if="showContextItems">
        <div class="bg-muted border-b px-4 py-2 text-xs font-medium">
          Context searches
        </div>
        <CommandGroup class="p-0">
          <CommandItem
            v-for="item in contextItems"
            :key="item.context"
            :value="`context:${item.context}`"
            class="group gap-2 rounded-none px-4 py-2"
            :data-testid="testIds.globalSearch.item.context(item.context)"
            @select="onContextSelect(item.context)"
          >
            <icon-search class="size-4" />
            <span v-if="!isSearchInputEmpty" class="text-sm">
              Search for "{{ searchInputValue }}" in {{ item.metadata.title }}
            </span>
            <span v-else class="text-sm">
              Search in {{ item.metadata.title }}
            </span>
            <Kbd class="ml-auto group-data-[highlighted]:hidden">
              {{ item.metadata.prefix }}:
            </Kbd>
            <Kbd class="ml-auto hidden group-data-[highlighted]:inline-flex">
              Enter
            </Kbd>
          </CommandItem>
        </CommandGroup>
      </template>

      <template
        v-for="resultGroup in groupedResults"
        :key="resultGroup.context"
      >
        <div
          class="bg-muted flex items-center gap-2 border-b px-4 py-2 text-xs font-medium"
          :data-testid="testIds.globalSearch.groupHeader(resultGroup.context)"
        >
          <span>{{ resultGroup.title }}</span>
          <span class="text-muted-foreground ml-auto">
            {{ resultsCountText(resultGroup.total) }}
          </span>
        </div>
        <CommandGroup class="p-0">
          <CommandItem
            v-for="result in resultGroup.visible"
            :key="result.id"
            :value="`result:${result.id}`"
            class="group gap-3 rounded-none px-4 py-2"
            :data-testid="testIds.globalSearch.item.result(result.id)"
            @select="onResultSelect(result)"
          >
            <component :is="result.icon" v-if="result.icon" class="size-4" />
            <div class="flex flex-col">
              <div class="text-sm">{{ result.title }}</div>
              <div v-if="result.subTitle" class="text-muted-foreground text-xs">
                {{ result.subTitle }}
              </div>
            </div>
            <Kbd class="ml-auto hidden group-data-[highlighted]:inline-flex">
              Enter
            </Kbd>
          </CommandItem>
          <CommandItem
            v-if="resultGroup.hasMore"
            :value="`more:${resultGroup.context}`"
            class="group gap-3 rounded-none px-4 py-2"
            :data-testid="
              testIds.globalSearch.item.showAll(resultGroup.context)
            "
            @select="onShowAll(resultGroup.context)"
          >
            <icon-search class="size-4" />
            <div class="flex flex-col">
              <div class="text-sm">
                Show all {{ resultGroup.total }} results
              </div>
              <div class="text-muted-foreground text-xs">
                {{ resultGroup.title }}
              </div>
            </div>
            <Kbd class="ml-auto hidden group-data-[highlighted]:inline-flex">
              Enter
            </Kbd>
          </CommandItem>
        </CommandGroup>
      </template>

      <CommandGroup v-if="flatResults.length > 0" class="p-0">
        <CommandItem
          v-for="result in flatResults"
          :key="result.id"
          :value="`result:${result.id}`"
          class="group gap-3 rounded-none px-4 py-2"
          :data-testid="testIds.globalSearch.item.result(result.id)"
          @select="onResultSelect(result)"
        >
          <component :is="result.icon" v-if="result.icon" class="size-4" />
          <div class="flex flex-col">
            <div class="text-sm">{{ result.title }}</div>
            <div v-if="result.subTitle" class="text-muted-foreground text-xs">
              {{ result.subTitle }}
            </div>
          </div>
          <Kbd class="ml-auto hidden group-data-[highlighted]:inline-flex">
            Enter
          </Kbd>
        </CommandItem>
      </CommandGroup>

      <div
        v-if="isSearching"
        class="flex flex-col gap-2 p-4"
        :data-testid="testIds.globalSearch.searching"
      >
        <div class="text-xs font-medium">Searching...</div>
      </div>
      <div
        v-else-if="showNoResults"
        class="flex flex-col gap-2 p-4"
        :data-testid="testIds.globalSearch.noResults"
      >
        <div class="text-xs font-medium">
          No results found for "{{ searchInputValue }}" in
          {{ contextMetadata[selectedSearchContext].title }}
        </div>
      </div>
    </CommandList>

    <div class="bg-muted/50 flex flex-col gap-2 border-t py-2 pl-4 pr-2">
      <div class="text-muted-foreground flex items-center gap-4 text-xs">
        <div class="flex flex-col items-center justify-center gap-1">
          <Kbd>
            <icon-arrow-down class="size-4" />
          </Kbd>
          <div class="text-muted-foreground text-xs">down</div>
        </div>
        <div class="flex flex-col items-center justify-center gap-1">
          <Kbd>
            <icon-arrow-up class="size-4" />
          </Kbd>
          <div class="text-muted-foreground text-xs">up</div>
        </div>
        <div class="flex w-fit flex-col items-center justify-center gap-1">
          <Kbd>esc</Kbd>
          <div class="text-muted-foreground text-xs">close</div>
        </div>
        <GlobalSearchHelpDialog>
          <template #trigger>
            <button
              type="button"
              class="hover:bg-accent hover:text-foreground ml-auto flex w-fit cursor-pointer select-none flex-col items-center justify-center gap-1 rounded-md p-2 px-4 transition-colors"
            >
              <icon-question class="size-4" />
              <div class="text-xs">help</div>
            </button>
          </template>
        </GlobalSearchHelpDialog>
      </div>
    </div>
  </Command>
</template>
